import { KeyboardEvent, useEffect, useRef, useState } from 'react';

type Segment = 'hours' | 'minutes' | 'seconds';

const RANGE: Record<Segment, number> = { hours: 23, minutes: 59, seconds: 59 };
const NAMES: Record<Segment, string> = { hours: 'hours', minutes: 'minutes', seconds: 'seconds' };

const pad = (value: number) => String(value).padStart(2, '0');
const partOf = (date: Date, segment: Segment) =>
  segment === 'hours' ? date.getHours() : segment === 'minutes' ? date.getMinutes() : date.getSeconds();

export type OtsTimeSegmentInputProps = {
  /** The date and time being edited, epoch milliseconds; typing without one starts from today. */
  value?: number;
  onChange: (value: number) => void;
  /** Names the group and each box, e.g. "Start" gives "Start hours". */
  label: string;
  /** Adds a seconds box after the minutes. */
  showSeconds?: boolean;
  /** What the arrow keys and the wheel step minutes by. */
  minuteStep?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Takes the font size of the text around it; kept for callers that size it themselves. */
  size?: 'compact' | 'default';
  className?: string;
};

const clamp = (value: number, min?: number, max?: number) =>
  Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? Number.NEGATIVE_INFINITY, value));

/**
 * Hours, minutes, and optionally seconds as separate boxes, as WaterGo's date-time input has them: click one and
 * type, step it with the arrow keys or the wheel, and move on with the arrows, Tab, or ":". Two digits, or one
 * that can't start a larger number, move on by themselves. Stepping past either end carries into the next unit.
 */
export const OtsTimeSegmentInput = ({
  value,
  onChange,
  label,
  showSeconds = false,
  minuteStep = 1,
  min,
  max,
  disabled = false,
  className,
}: OtsTimeSegmentInputProps) => {
  const segments: Segment[] = showSeconds ? ['hours', 'minutes', 'seconds'] : ['hours', 'minutes'];
  const refs = useRef<Partial<Record<Segment, HTMLInputElement | null>>>({});
  // What has been typed into the box being edited, before it is committed.
  // Kept in a ref too: moving to the next box blurs this one in the same event, before a re-render, and the blur
  // must see that the typing was already committed.
  const [typing, setTypingState] = useState<{ segment: Segment; text: string }>();
  const typingRef = useRef(typing);
  const setTyping = (next: { segment: Segment; text: string } | undefined) => {
    typingRef.current = next;
    setTypingState(next);
  };
  const current = value === undefined ? undefined : new Date(value);

  const base = () => {
    if (value !== undefined) return new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const commit = (date: Date) => onChange(clamp(date.getTime(), min, max));

  const setPart = (segment: Segment, part: number) => {
    const date = base();
    const bounded = Math.min(RANGE[segment], Math.max(0, part));
    if (segment === 'hours') date.setHours(bounded);
    else if (segment === 'minutes') date.setMinutes(bounded);
    else date.setSeconds(bounded);
    if (!showSeconds) date.setSeconds(0);
    commit(date);
  };

  // Stepping goes through Date, so 23:59 plus a minute is tomorrow's 00:00.
  const step = (segment: Segment, direction: 1 | -1) => {
    const date = base();
    if (segment === 'hours') date.setHours(date.getHours() + direction);
    else if (segment === 'minutes') date.setMinutes(date.getMinutes() + direction * minuteStep);
    else date.setSeconds(date.getSeconds() + direction);
    commit(date);
  };

  const focusSegment = (index: number) => {
    const target = segments[index];
    if (target) refs.current[target]?.focus();
  };

  const finishTyping = () => {
    const pending = typingRef.current;
    setTyping(undefined);
    if (pending?.text) setPart(pending.segment, Number(pending.text));
  };

  const typeDigit = (segment: Segment, index: number, digit: string) => {
    const pending = typingRef.current;
    const text = (pending?.segment === segment ? pending.text : '') + digit;
    // "7" can only be 07 in hours (or 70+ in minutes is impossible), so it moves on at once.
    const done = text.length === 2 || Number(`${text}0`) > RANGE[segment];
    if (done) {
      setTyping(undefined);
      setPart(segment, Number(text));
      focusSegment(index + 1);
    } else {
      setTyping({ segment, text });
    }
  };

  const onKeyDown = (segment: Segment, index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      typeDigit(segment, index, event.key);
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        setTyping(undefined);
        step(segment, event.key === 'ArrowUp' ? 1 : -1);
        return;
      case 'ArrowLeft':
        event.preventDefault();
        finishTyping();
        focusSegment(index - 1);
        return;
      case 'ArrowRight':
      case ':':
        event.preventDefault();
        finishTyping();
        focusSegment(index + 1);
        return;
      case 'Backspace':
      case 'Delete':
        event.preventDefault();
        if (typingRef.current?.segment === segment && typingRef.current.text) setTyping({ segment, text: typingRef.current.text.slice(0, -1) });
        else if (event.key === 'Backspace') focusSegment(index - 1);
        return;
      case 'Enter':
        finishTyping();
        return;
      case 'Escape':
        if (typingRef.current) {
          event.preventDefault();
          event.stopPropagation();
          setTyping(undefined);
        }
        return;
      case 'Tab':
        finishTyping();
        return;
      default:
        // Letters and the rest would only be thrown away.
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) event.preventDefault();
    }
  };

  // The wheel steps the box under the pointer while it has focus, as WaterGo's does; a passive listener can't
  // stop the page from scrolling, so it is attached by hand.
  const latest = useRef(step);
  latest.current = step;
  useEffect(() => {
    const cleanups = segments.map((segment) => {
      const input = refs.current[segment];
      if (!input) return () => undefined;
      const onWheel = (event: WheelEvent) => {
        if (document.activeElement !== input || disabled) return;
        event.preventDefault();
        latest.current(segment, event.deltaY < 0 ? 1 : -1);
      };
      input.addEventListener('wheel', onWheel, { passive: false });
      return () => input.removeEventListener('wheel', onWheel);
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [showSeconds, disabled]);

  return (
    <span role="group" aria-label={`${label} time`} className={['inline-flex items-center tabular-nums', className].filter(Boolean).join(' ')}>
      {segments.map((segment, index) => {
        const shown = typing?.segment === segment
          ? typing.text.padStart(2, '0')
          : current ? pad(partOf(current, segment)) : '';
        return (
          <span key={segment} className="inline-flex items-center">
            {index > 0 && <span aria-hidden="true" className="px-px text-[var(--ots-text-muted)]">:</span>}
            <input
              ref={(element) => { refs.current[segment] = element; }}
              type="text"
              inputMode="numeric"
              aria-label={`${label} ${NAMES[segment]}`}
              role="spinbutton"
              aria-valuemin={0}
              aria-valuemax={RANGE[segment]}
              aria-valuenow={current ? partOf(current, segment) : undefined}
              placeholder="--"
              disabled={disabled}
              value={shown}
              // Keyboards that send text without key presses (phones, paste, dictation) come through here.
              onChange={(event) => {
                const inserted = (event.nativeEvent as InputEvent).data ?? event.target.value;
                for (const digit of (inserted ?? '').replace(/\D/g, '').slice(0, 2)) typeDigit(segment, index, digit);
              }}
              onKeyDown={onKeyDown(segment, index)}
              onFocus={(event) => event.currentTarget.select()}
              onClick={(event) => event.currentTarget.select()}
              onBlur={finishTyping}
              className={[
                // Two digits wide whatever the font size, so the field can shrink and grow around it. The font comes
                // from the field (inputs don't inherit it by default, and host resets set their own), so the time is
                // the same size and face as the date beside it.
                '!m-0 w-[2.6ch] rounded-sm !border-0 bg-transparent !p-0 text-center outline-none caret-transparent shadow-none',
                '[font:inherit] [letter-spacing:inherit] text-[var(--ots-text)]',
                'selection:bg-[var(--ots-primary-container)] focus:bg-[var(--ots-primary-container)] focus:text-[var(--ots-on-primary-container)]',
                'placeholder:text-[var(--ots-text-muted)] disabled:cursor-not-allowed',
              ].join(' ')}
            />
          </span>
        );
      })}
    </span>
  );
};
